'use client';
import Button from '@/components/Button';
import { GameHubConnectionProvider } from '@/components/GameHubConnection';
import MessageToUSer from '@/components/layout/MessageToUSer';
import Spinner from '@/components/Spinner';
import { AutoplayProvider } from '@/components/tirageEnCours/AutoplayProvider';
import { gameRealtimeManager } from '@/game-realtime';
import useNavigate from '@/hooks/useNavigate';
import Errors from '@/services/errors';
import { getLiveGameMessages, getLiveGameModel } from '@/services/game';
import { getCookie, setCookie } from '@/utils/util';
import { useSession } from 'next-auth/react';
import { lazy, Suspense, useContext, useEffect, useRef, useState } from 'react';
import { ToastContext } from './Layout';
import LegalDoc from './legals/LegalDoc';

const MainTirageComponent = lazy(() => import('@/components/tirageEnCours/MainTirageComponent'));

const TirageEnCours = ({ searchParams }) => {
  const [monitorState, setMonitorState] = useState(false);

  const navigate = useNavigate();
  const updateToast = useContext(ToastContext);
  const { data: session } = useSession();

  const [model, setModel] = useState();
  const [readNotice, setReadNotice] = useState(false);
  const [messageToDisplay, setMessageToDisplay] = useState({
    message: '',
    status: 'info',
    show: false,
  });
  const [clientMessageToDisplay, setClientMessageToDisplay] = useState({
    message: '',
    status: 'info',
    show: false,
  });
  const [HTMLmessageToDisplay, HTMLsetMessageToDisplay] = useState({
    message: '',
    status: 'info',
    show: false,
  });
  const [audioFilePath, setAudioFilePath] = useState();
  const [isBouleFrimee, setIsBouleFrimee] = useState(false);
  const [messages, setMessages] = useState([]);

  const modelRef = useRef();
  modelRef.current = model;
  const audioFilePathRef = useRef();
  audioFilePathRef.current = audioFilePath;
  const isBouleFrimeeRef = useRef();
  isBouleFrimeeRef.current = isBouleFrimee;

  const fetchModel = async () => {
    try {
      const userAgent = navigator?.userAgent || '';
      const response = await getLiveGameModel((userAgent && typeof userAgent === 'string') || '');
      if (response instanceof Error) {
        Errors.handleError(response, updateToast);
        return null;
      }
      setModel(response);
      window.model = response;
      // Mettre à jour le modèle dans le manager
      gameRealtimeManager.setModel(response);
      return response;
    } catch (e) {
      Errors.handleError(e, updateToast);
      return null;
    }
  };
  const fetchMessages = async () => {
    try {
      const response = await getLiveGameMessages();
      if (response instanceof Error) {
        return Errors.handleError(response, updateToast);
      }
      setMessages(response);
    } catch (e) {
      Errors.handleError(e, updateToast);
    }
  };

  function setShowHide(model) {
    try {
      var isActif = model?.isActifTirage;
      if (!isActif) {
        setTimeout(() => {
          navigate('/');
        }, 5000);
        return;
      }
      const isNoticeReadCookieValue = getCookie('isNoticeRead');
      setReadNotice(isNoticeReadCookieValue === 'true');
      if (isNoticeReadCookieValue === 'true') {
        showPlayerElement();
      }
    } catch (e) {
      Errors.handleError(e, updateToast);
    }
  }

  function noticeRead() {
    setCookie('isNoticeRead', true, 1);
    setReadNotice(true);
    showPlayerElement();
  }

  function showPlayerElement() {
    document.getElementById('audio-player-fixed').style.display = 'block';
  }

  // Initialiser le modèle et les messages au chargement
  useEffect(() => {
    try {
      Promise.all([fetchModel(), fetchMessages()]).then((results) => {
        // Après avoir récupéré le modèle, le passer au manager
        if (results[0] && !(results[0] instanceof Error)) {
          gameRealtimeManager.setModel(results[0]);
        }
      });
    } catch (e) {
      console.error(e);
      Errors.handleError(e, updateToast);
    }
  }, []);

  useEffect(() => {
    try {
      if (model != null) {
        setShowHide(model);
      }
    } catch (e) {
      console.error(e);
      Errors.handleError(e, updateToast);
    }
  }, [model]);

  // Initialiser le GameRealtimeManager
  useEffect(() => {
    let isMounted = true;

    const initManager = async () => {
      try {
        await gameRealtimeManager.initialize({
          setModel: setModel,
          setMessageToDisplay: setClientMessageToDisplay,
          fetchModel: fetchModel,
          navigate: navigate,
          userEmail: session?.user?.email,
        });
      } catch (e) {
        console.error('Erreur lors de l\'initialisation du manager:', e);
        Errors.handleError(e, updateToast);
      }
    };

    if (isMounted) {
      initManager();
    }

    // Ne pas nettoyer les connexions au démontage car elles sont partagées
    // Les connexions seront fermées seulement à la fermeture de la page
    return () => {
      isMounted = false;
      // Nettoyer seulement les callbacks, pas les connexions
      gameRealtimeManager.cleanup();
    };
  }, []);

  useEffect(() => {
    setMonitorState((old) => !old);
  }, [clientMessageToDisplay]);

  useEffect(() => {
    if (window) {
      const dotnetScripts = [
        // '/js/dotnet/chatClient.js', // COMMENTÉ - Le nouveau système game-realtime le remplace
        // Test: Vérifier si le retrait de ce script ne brise rien
        '/js/dotnet/Bingo.js',
        '/js/dotnet/audio.js',
        '/js/dotnet/onload.js',
        '/js/dotnet/visibility.js',
      ];

      dotnetScripts.forEach((s) => {
        const script = document.createElement('script');
        script.src = s;
        script.async = true;
        document.getElementById('root').parentElement.appendChild(script);
      });
    }
  }, []);

  return (
    <>
      <MessageToUSer
        message={messageToDisplay.message}
        showMessage={messageToDisplay.show}
        status={messageToDisplay.status}
        position={{ position: 'fixed', place: 'top-center' }}
        className={'min-h-100px w-50'}
        onDismiss={messageToDisplay.onDismiss}
      />
      <MessageToUSer
        message={clientMessageToDisplay.message}
        showMessage={clientMessageToDisplay.show}
        status={clientMessageToDisplay.status}
        position={{ position: 'fixed', place: 'center' }}
        className={'min-h-100px'}
        key={monitorState}
        onDismiss={clientMessageToDisplay.onDismiss}
      />
      {model ? (
        <>
          {!model?.isActifTirage ? (
            <>
              <div className="card card-3-light mt-3 p-4">
                <div className="card-title">
                  <h2 className="text-center">Il n&apos;y a pas de tirage actif présentement.</h2>
                </div>
                {model?.isProchainTirage && (
                  <div className="card-body">
                    <p className="text-center">
                      Le prochain tirage est prévu pour le {model.prochainTirage.dateOuvertureString}
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {!readNotice ? (
                <>
                  <LegalDoc id="2" />
                  <div className="row justify-content-end mb-2  m-1 m-md-2 m-lg-4">
                    <div className="col-4 d-flex justify-content-end">
                      <Button
                        type="button"
                        className="btn btn-primary"
                        onClick={
                          noticeRead ??
                          (() => {
                            console.warn('Notice Read Is Undefined');
                          })
                        }
                      >
                        J&apos;ai lu et accepté l&apos;avis légal
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <Suspense fallback={<Spinner />}>
                  {readNotice && (
                    <MainTirageComponent
                      model={model}
                      setMessageToDisplay={setClientMessageToDisplay}
                      messages={messages}
                    />
                  )}
                </Suspense>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <Spinner />
        </>
      )}
    </>
  );
};

/* eslint-disable */
export default ({ searchParams }) => (
  <AutoplayProvider>
    <GameHubConnectionProvider>
      <TirageEnCours searchParams={searchParams} />
    </GameHubConnectionProvider>
  </AutoplayProvider>
);
